// features/procurement/ReturnListPage.tsx
/**
 * Vendor Returns & Claims Management — Executive UI/UX Redesign
 * Featuring origin GRN tracking, instant approvals, stock deduction disclaimers,
 * and zero-silent-fail error handling.
 */

import { useState, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuthStore } from '@/store/authStore';
import { useToastStore } from '@/store/toastStore';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/Badge';
import { Spinner } from '@/components/ui/Spinner';
import { EmptyState } from '@/components/ui/EmptyState';
import { Card } from '@/components/ui/Card';
import {
    RotateCcw, Plus, Search, X, RefreshCw,
    CheckCircle, Clock, Package, Truck, ArrowLeft,
    Building2, ChevronRight, AlertCircle, DollarSign,
    ShieldCheck, Store, ArrowUpRight
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { clsx } from 'clsx';
import {
    getReturns,
    approveReturn,
    getProcurementErrorMessage,
    PurchaseReturn,
} from './services/procurementApi';

// ============================================================
// STATUS PILL COMPONENT
// ============================================================

const STATUS_CONFIG: Record<string, { label: string; bg: string; text: string; dot: string }> = {
    draft: { label: 'Draft (Pending)', bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
    approved: { label: 'Approved (Deducted)', bg: 'bg-blue-50', text: 'text-blue-700', dot: 'bg-blue-500' },
    shipped: { label: 'Shipped to Vendor', bg: 'bg-purple-50', text: 'text-purple-700', dot: 'bg-purple-500' },
    received_by_vendor: { label: 'Received by Vendor', bg: 'bg-cyan-50', text: 'text-cyan-700', dot: 'bg-cyan-500' },
    credit_note_received: { label: 'Credit Note Received', bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
};

function StatusPill({ status }: { status: string }) {
    const config = STATUS_CONFIG[status] || {
        label: status.replace(/_/g, ' '),
        bg: 'bg-slate-100',
        text: 'text-slate-700',
        dot: 'bg-slate-400',
    };

    return (
        <span className={clsx('inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold', config.bg, config.text)}>
            <span className={clsx('w-1.5 h-1.5 rounded-full', config.dot)} />
            {config.label}
        </span>
    );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function ReturnListPage() {
    const user = useAuthStore((s) => s.user);
    const tenantId = user?.tenantId || '';
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const addToast = useToastStore((s) => s.addToast);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('all');
    const [approveTarget, setApproveTarget] = useState<PurchaseReturn | null>(null);

    // ============================================================
    // QUERY
    // ============================================================

    const { data: returns, isLoading, refetch } = useQuery<PurchaseReturn[]>({
        queryKey: ['returns-list', tenantId],
        queryFn: () => getReturns(tenantId, { limit: 100 }),
        enabled: !!tenantId,
    });

    // ============================================================
    // MUTATION
    // ============================================================

    const approveMutation = useMutation({
        mutationFn: (returnId: string) => approveReturn(tenantId, returnId),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['returns-list'] });
            queryClient.invalidateQueries({ queryKey: ['dashboard-returns'] });
            queryClient.invalidateQueries({ queryKey: ['stock-levels'] });
            setApproveTarget(null);
            addToast({
                type: 'success',
                title: 'Return Approved',
                message: 'Inventory has been deducted and vendor claim logged.',
            });
        },
        onError: (err: any) => {
            setApproveTarget(null);
            addToast({
                type: 'error',
                title: 'Approval Failed',
                message: getProcurementErrorMessage(err, 'Could not approve vendor return.'),
            });
        },
    });

    // ============================================================
    // COMPUTED STATS & FILTERING
    // ============================================================

    const returnsList = returns || [];

    const filteredReturns = useMemo(() => {
        return returnsList.filter((r) => {
            const matchesSearch =
                !search ||
                r.returnNumber?.toLowerCase().includes(search.toLowerCase()) ||
                r.vendorName?.toLowerCase().includes(search.toLowerCase()) ||
                r.reason?.toLowerCase().includes(search.toLowerCase());

            const matchesStatus =
                statusFilter === 'all' || r.status === statusFilter;

            return matchesSearch && matchesStatus;
        });
    }, [returnsList, search, statusFilter]);

    const draftCount = returnsList.filter((r) => r.status === 'draft').length;
    const approvedCount = returnsList.filter((r) => r.status === 'approved').length;
    const completedCount = returnsList.filter((r) => r.status === 'credit_note_received').length;

    const totalReturnValue = returnsList.reduce((sum, r) => {
        const directTotal = parseFloat((r as any).totalAmount || '0');
        if (!isNaN(directTotal) && directTotal > 0) return sum + directTotal;
        const itemSum = r.items?.reduce((s, it) => s + parseFloat(it.quantity || '0') * parseFloat(it.unitCost || '0'), 0) || 0;
        return sum + itemSum;
    }, 0);

    const formatNaira = (val: number) =>
        `₦${val.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

    const formatDate = (dateStr?: string) =>
        dateStr ? new Date(dateStr).toLocaleDateString('en-NG', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

    return (
        <div className="min-h-screen bg-slate-50/60 pb-16">
            {/* Header */}
            <div className="bg-white/95 backdrop-blur-md border-b border-slate-200/80 sticky top-0 z-30 shadow-sm">
                <div className="max-w-[1680px] mx-auto px-6 lg:px-8 py-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <button
                                onClick={() => navigate('/procurement')}
                                className="p-2 text-slate-500 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition"
                                type="button"
                                title="Back to Procurement"
                            >
                                <ArrowLeft size={20} />
                            </button>
                            <div className="p-3 bg-gradient-to-br from-rose-500 to-pink-600 rounded-2xl shadow-lg shadow-rose-500/20 text-white">
                                <RotateCcw className="w-6 h-6" />
                            </div>
                            <div>
                                <h1 className="text-2xl font-black text-slate-900 tracking-tight">Vendor Returns & Claims</h1>
                                <p className="text-xs text-slate-500 mt-0.5">
                                    {returnsList.length} total claims • {draftCount} pending approval
                                </p>
                            </div>
                        </div>

                        <div className="flex items-center gap-2.5">
                            <button
                                onClick={() => refetch()}
                                className="p-2.5 text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition"
                                title="Refresh Returns"
                                type="button"
                            >
                                <RefreshCw size={15} />
                            </button>

                            <button
                                onClick={() => navigate('/procurement/returns/new')}
                                className="px-4 py-2.5 bg-gradient-to-r from-rose-600 to-pink-600 text-white rounded-xl hover:from-rose-700 hover:to-pink-700 transition font-bold text-xs flex items-center gap-2 shadow-md shadow-rose-500/20"
                                type="button"
                            >
                                <Plus size={16} />
                                <span>Create Return</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1680px] mx-auto px-6 lg:px-8 pt-6 space-y-6">

                {/* Metric Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Claims</span>
                        <p className="text-2xl font-black text-slate-900 mt-1">{returnsList.length}</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Total Return Value</span>
                        <p className="text-2xl font-black text-rose-600 mt-1 truncate">{formatNaira(totalReturnValue)}</p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Awaiting Approval</span>
                        <p className={clsx('text-2xl font-black mt-1', draftCount > 0 ? 'text-amber-600' : 'text-slate-900')}>
                            {draftCount}
                        </p>
                    </div>
                    <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm">
                        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-wider">Credit Received</span>
                        <p className="text-2xl font-black text-emerald-600 mt-1">{completedCount}</p>
                    </div>
                </div>

                {/* Filter and Search Bar */}
                <div className="bg-white rounded-2xl border border-slate-200/90 p-4 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
                    <div className="relative w-full md:w-80">
                        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" />
                        <input
                            type="text"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            placeholder="Search by Return #, supplier, reason..."
                            className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-xl text-xs bg-slate-50/50 focus:bg-white focus:outline-none focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition"
                        />
                    </div>

                    {/* Filter Pills */}
                    <div className="flex items-center gap-1.5 overflow-x-auto w-full md:w-auto pb-1 md:pb-0 scrollbar-none">
                        {[
                            { id: 'all', label: 'All Returns' },
                            { id: 'draft', label: 'Draft' },
                            { id: 'approved', label: 'Approved' },
                            { id: 'shipped', label: 'Shipped' },
                            { id: 'credit_note_received', label: 'Credit Received' },
                        ].map((filter) => (
                            <button
                                key={filter.id}
                                onClick={() => setStatusFilter(filter.id)}
                                className={clsx(
                                    'px-3 py-1.5 rounded-xl text-xs font-semibold whitespace-nowrap transition-all',
                                    statusFilter === filter.id
                                        ? 'bg-rose-600 text-white shadow-sm'
                                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                                )}
                                type="button"
                            >
                                {filter.label}
                            </button>
                        ))}
                    </div>
                </div>

                {/* Table View */}
                {isLoading ? (
                    <div className="py-20 text-center flex flex-col items-center justify-center">
                        <Spinner size="lg" />
                        <p className="text-sm font-medium text-slate-500 mt-4">Loading purchase returns...</p>
                    </div>
                ) : filteredReturns.length === 0 ? (
                    <div className="bg-white rounded-2xl border border-slate-200 p-12 text-center">
                        <div className="w-16 h-16 rounded-2xl bg-rose-50 text-rose-600 flex items-center justify-center mx-auto mb-4">
                            <RotateCcw size={28} />
                        </div>
                        <h3 className="text-base font-bold text-slate-900">No Returns Found</h3>
                        <p className="text-xs text-slate-500 mt-1 max-w-sm mx-auto">
                            {search ? `No returns matching "${search}".` : 'Log vendor returns for damaged, defective, or rejected goods.'}
                        </p>
                        <button
                            onClick={() => navigate('/procurement/returns/new')}
                            className="mt-5 px-5 py-2.5 bg-rose-600 hover:bg-rose-700 text-white rounded-xl text-xs font-bold transition shadow-sm"
                            type="button"
                        >
                            + Create Vendor Return
                        </button>
                    </div>
                ) : (
                    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-sm overflow-hidden">
                        <div className="overflow-x-auto">
                            <table className="w-full text-left border-collapse">
                                <thead>
                                    <tr className="border-b border-slate-200 text-[11px] font-bold text-slate-500 uppercase tracking-wider bg-slate-50/70">
                                        <th className="py-3.5 px-6">Return Number</th>
                                        <th className="py-3.5 px-6">Supplier</th>
                                        <th className="py-3.5 px-6">Reason</th>
                                        <th className="py-3.5 px-6">Date</th>
                                        <th className="py-3.5 px-6">Status</th>
                                        <th className="py-3.5 px-6 text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="divide-y divide-slate-100 text-sm">
                                    {filteredReturns.map((ret) => (
                                        <tr
                                            key={ret.id}
                                            onClick={() => navigate(`/procurement/returns/${ret.id}`)}
                                            className="hover:bg-slate-50/80 transition-colors cursor-pointer group"
                                        >
                                            {/* Return Number */}
                                            <td className="py-4 px-6 font-mono font-bold text-slate-900">
                                                {ret.returnNumber}
                                            </td>

                                            {/* Supplier */}
                                            <td className="py-4 px-6">
                                                <div className="flex items-center gap-2.5">
                                                    <div className="w-7 h-7 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center font-bold text-xs flex-shrink-0">
                                                        {(ret.vendorName || 'S').slice(0, 1).toUpperCase()}
                                                    </div>
                                                    <span className="font-semibold text-slate-800">
                                                        {ret.vendorName || 'Supplier'}
                                                    </span>
                                                </div>
                                            </td>

                                            {/* Reason */}
                                            <td className="py-4 px-6 text-xs text-slate-600 max-w-xs truncate">
                                                <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-medium">
                                                    {ret.reason || 'Damaged goods'}
                                                </span>
                                            </td>

                                            {/* Date */}
                                            <td className="py-4 px-6 text-xs text-slate-500">
                                                {formatDate(ret.createdAt)}
                                            </td>

                                            {/* Status Badge */}
                                            <td className="py-4 px-6">
                                                <StatusPill status={ret.status || 'draft'} />
                                            </td>

                                            {/* Actions */}
                                            <td className="py-4 px-6 text-right" onClick={(e) => e.stopPropagation()}>
                                                <div className="flex items-center justify-end gap-1.5">
                                                    {ret.status === 'draft' && (
                                                        <button
                                                            onClick={() => setApproveTarget(ret)}
                                                            className="px-3 py-1 text-xs font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 hover:bg-emerald-100 rounded-lg transition flex items-center gap-1 shadow-sm"
                                                            type="button"
                                                            title="Approve Return & Deduct Stock"
                                                        >
                                                            <CheckCircle size={12} />
                                                            <span>Approve</span>
                                                        </button>
                                                    )}

                                                    <button
                                                        onClick={() => navigate(`/procurement/returns/${ret.id}`)}
                                                        className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition"
                                                        type="button"
                                                    >
                                                        <ChevronRight size={16} />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>

            {/* ============================================================ */}
            {/* APPROVE CONFIRMATION MODAL                                   */}
            {/* ============================================================ */}
            <AnimatePresence>
                {approveTarget && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
                        onClick={() => setApproveTarget(null)}
                    >
                        <motion.div
                            initial={{ scale: 0.95, opacity: 0, y: 15 }}
                            animate={{ scale: 1, opacity: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0, y: 15 }}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-white rounded-3xl shadow-2xl w-full max-w-sm p-6 text-center"
                        >
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ type: 'spring', delay: 0.1 }}
                                className="w-14 h-14 rounded-2xl bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto mb-4"
                            >
                                <CheckCircle size={26} />
                            </motion.div>
                            <h3 className="text-base font-bold text-slate-900">
                                Approve Return #{approveTarget.returnNumber}?
                            </h3>
                            <p className="text-xs text-slate-500 mt-2">
                                Approving this claim will immediately deduct returned items from warehouse inventory and generate a credit claim against <strong>{approveTarget.vendorName}</strong>.
                            </p>

                            <div className="flex items-center gap-2.5 mt-6 pt-3 border-t border-slate-100">
                                <button
                                    onClick={() => setApproveTarget(null)}
                                    className="flex-1 py-2.5 border border-slate-200 rounded-xl text-xs font-semibold text-slate-600 hover:bg-slate-50 transition"
                                    type="button"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={() => approveMutation.mutate(approveTarget.id)}
                                    disabled={approveMutation.isPending}
                                    className="flex-1 py-2.5 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-xl text-xs font-bold hover:from-emerald-600 transition shadow-md shadow-emerald-500/20 flex items-center justify-center gap-2 disabled:opacity-50"
                                    type="button"
                                >
                                    {approveMutation.isPending ? <Spinner size="xs" variant="white" /> : <CheckCircle size={14} />}
                                    <span>Approve & Deduct</span>
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}